import { useState } from 'react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { cn } from '@/lib/cn';

// Only two sections live here — Profile / Users & roles / API keys belong
// to the legacy ufanisi back office and other internal tooling, so we
// don't duplicate them.
type Section = 'workflows' | 'replies';

const SECTIONS: { id: Section; label: string }[] = [
  { id: 'workflows', label: 'Service workflows' },
  { id: 'replies', label: 'Canned replies' },
];

export function SettingsPage() {
  const [section, setSection] = useState<Section>('workflows');
  return (
    <div className="grid grid-cols-[220px_1fr] gap-6">
      <nav className="flex flex-col gap-0.5">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSection(s.id)}
            className={cn(
              'text-left h-9 px-3 rounded-md text-body transition-colors',
              section === s.id
                ? 'bg-status-info-bg text-brand-blue font-semibold'
                : 'text-ink hover:bg-canvas',
            )}
          >
            {s.label}
          </button>
        ))}
      </nav>
      <div>
        {section === 'workflows' && <WorkflowsSection />}
        {section === 'replies' && <RepliesSection />}
      </div>
    </div>
  );
}

function WorkflowsSection() {
  return (
    <Card>
      <CardHeader
        title="Service workflows"
        description="Auto-approval thresholds and SLAs per service type."
      />
      <div className="space-y-3">
        {['Exhauster', 'Bowser', 'Water tokens', 'Meter test', 'Meter replacement'].map((s, i) => (
          <div key={s} className="flex items-center justify-between gap-4 rounded-lg border border-line p-4">
            <div>
              <div className="text-body-strong text-ink">{s}</div>
              <div className="text-caption text-ink-muted">
                Auto-approve under KES {(i + 1) * 2000} · SLA {2 + i} business days
              </div>
            </div>
            <Checkbox defaultChecked={i % 2 === 0} label="Auto-approve" />
          </div>
        ))}
      </div>
    </Card>
  );
}

function RepliesSection() {
  return (
    <Card>
      <CardHeader
        title="Canned replies"
        description="Quick responses staff can drop into customer chats."
        actions={<Button size="sm" variant="secondary">Add reply</Button>}
      />
      <ul className="space-y-2">
        {[
          { label: 'Field team scheduled', body: "Hi, we've scheduled the field team for today between 10 AM and 1 PM. We'll SMS you when the truck leaves the depot." },
          { label: 'Payment received',     body: 'Your payment has been received and reflected on your account. A receipt has been emailed to you.' },
          { label: 'Awaiting documents',   body: "We're still waiting on the ID and KRA documents you uploaded — please check the photos are clear and re-upload if needed." },
        ].map((r) => (
          <li key={r.label} className="rounded-lg border border-line p-3">
            <div className="text-body-strong text-ink">{r.label}</div>
            <div className="text-caption text-ink-muted mt-1">{r.body}</div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
